import React from 'react';
import { LoadingState } from '../../../components/common/EmptyState';
import { PeriodSelector } from '../../../components/common/PeriodSelector';
import { EmailConfigButton, EnviarFacturaCell } from '../../../components/analista/FacturaEmail';
import { ContratoInfoDialog, MetodoFacturaBadge } from '../../../components/analista/ContratoInfoDialog';
import { HistorialNetoButton } from '../../../components/analista/HistorialNeto';
import { HistorialFCButton } from '../../../components/analista/HistorialFC';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import { colppyApi, facturacionApi } from '../../../lib/api';
import { cn, formatImporte, getFacturacionPeriods, formatDate } from '../../../lib/utils';
import { AlertTriangle, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, FileText, Pencil, Upload, X, Zap } from 'lucide-react';

// Celda editable de monto neto
const MontoNetoCell = ({ item, edificioId, selectedPeriodo, setFacturacionData, toast, formatImporte }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef(null);
  const savingRef = React.useRef(false);

  const handleEdit = () => {
    setValue(String(item.monto_neto || 0));
    setEditing(true);
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 50);
  };

  const doSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const nuevoMonto = parseFloat(value);
    if (!isNaN(nuevoMonto) && nuevoMonto >= 0 && nuevoMonto !== item.monto_neto) {
      try {
        await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { monto_manual: nuevoMonto });
        const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
        setFacturacionData(res.data);
        toast.success('Monto actualizado');
      } catch (err) { toast.error('Error al guardar monto'); }
    }
    savingRef.current = false;
  };

  const handleClear = async () => {
    try {
      await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { monto_manual: -1 });
      const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
      setFacturacionData(res.data);
      toast.success('Monto restaurado del contrato');
    } catch (err) { toast.error('Error al restaurar monto'); }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      doSave();
      setEditing(false);
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      doSave();
      setEditing(false);
    }
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <Input ref={inputRef} type="number" value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs w-[120px] text-right font-mono"
        data-testid={`monto-input-${item.unidad_id}`}
      />
    );
  }

  const esManual = item.monto_manual !== null && item.monto_manual !== undefined && item.monto_manual >= 0;
  const esUSD = item.moneda_original === 'USD' && item.cotizacion_usd;
  return (
    <span className="flex flex-col items-end w-full">
    <span className="flex items-center justify-end gap-1 w-full cursor-pointer" onClick={handleEdit}>
      <span className={cn("hover:underline", esManual ? "text-blue-700 font-semibold" : "text-slate-900")}>
        {item.moneda === 'USD' ? 'US$ ' : '$ '}
        {formatImporte(item.monto_neto, 2)}
      </span>
      {esManual && (
        <button onClick={(e) => { e.stopPropagation(); handleClear(); }} className="text-blue-400 hover:text-red-500 transition-colors" title="Restaurar monto del contrato" data-testid={`monto-clear-${item.unidad_id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
      {!esManual && (
        <Pencil className="w-3 h-3 text-slate-300" />
      )}
    </span>
    {esUSD && (
      <span className="text-[10px] text-slate-400" data-testid={`monto-usd-origen-${item.unidad_id}`}>
        US$ {formatImporte(item.monto_usd, 2)} @ ${formatImporte(item.cotizacion_usd, 2)}{item.cotizacion_periodo ? ` (${item.cotizacion_periodo})` : ''}
      </span>
    )}
    </span>
  );
};

// Celda editable de intereses
const InteresesCell = ({ item, edificioId, selectedPeriodo, setFacturacionData, toast, formatImporte }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const inputRef = React.useRef(null);
  const savingRef = React.useRef(false);

  const handleEdit = () => {
    setValue(String(item.intereses || 0));
    setEditing(true);
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 50);
  };

  const doSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const nuevo = parseFloat(value);
    if (!isNaN(nuevo) && nuevo >= 0 && nuevo !== item.intereses) {
      try {
        await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { intereses_manual: nuevo });
        const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
        setFacturacionData(res.data);
        toast.success('Intereses actualizados');
      } catch (err) { toast.error('Error al guardar intereses'); }
    }
    savingRef.current = false;
  };

  const handleClear = async () => {
    try {
      await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { intereses_manual: -1 });
      const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
      setFacturacionData(res.data);
      toast.success('Intereses restaurados');
    } catch (err) { toast.error('Error al restaurar'); }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      doSave();
      setEditing(false);
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      doSave();
      setEditing(false);
    }
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <Input ref={inputRef} type="number" value={value} onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs w-[110px] text-right font-mono" data-testid={`intereses-input-${item.unidad_id}`} />
    );
  }

  const esManual = item.intereses_manual !== null && item.intereses_manual !== undefined && item.intereses_manual >= 0;
  return (
    <span className="flex items-center justify-end gap-1 w-full cursor-pointer" onClick={handleEdit}>
      <span className={cn("hover:underline", esManual ? "text-blue-700 font-semibold" : "text-orange-600")}>
        $ {formatImporte(item.intereses, 2)}
      </span>
      {esManual && (
        <button onClick={(e) => { e.stopPropagation(); handleClear(); }} className="text-blue-400 hover:text-red-500 transition-colors" title="Restaurar intereses calculados">
          <X className="w-3 h-3" />
        </button>
      )}
      {!esManual && (
        <Pencil className="w-3 h-3 text-orange-300" />
      )}
    </span>
  );
};

// Celda editable para OC mensual
const OcMensualCell = ({ item, edificioId, selectedPeriodo, setFacturacionData, toast }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(item.orden_compra_mensual || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => { setValue(item.orden_compra_mensual || ''); }, [item.orden_compra_mensual]);

  const doSave = async () => {
    if (value !== (item.orden_compra_mensual || '')) {
      try {
        await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { orden_compra_mensual: value });
        const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
        setFacturacionData(res.data);
      } catch (err) { toast.error('Error al guardar OC'); }
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)}
        onBlur={doSave} onKeyDown={(e) => { if (e.key === 'Enter') doSave(); if (e.key === 'Escape') setEditing(false); }}
        className="w-20 text-xs text-center border border-slate-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#1B4D3E]"
        autoFocus placeholder="OC..." />
    );
  }

  return (
    <span className="text-xs font-mono text-slate-600 cursor-pointer hover:text-[#1B4D3E]" onClick={() => setEditing(true)}>
      {item.orden_compra_mensual || <span className="text-slate-300 italic">Cargar</span>}
    </span>
  );
};

export const TabFactura = ({ cn, edificioId, error, expandedGrupos, facturacionData, facturasCreadas, filtroEstadoFactura, formatImporte, getGroupedFacItems, loading, loadingFacturacion, navigatePeriodo, selectedPeriodo, setFacturacionData, setFacturasCreadas, setSelectedPeriodo, toast, toggleGrupoExpanded, unidades, contratos }) => {
  const [colppyError, setColppyError] = React.useState(null);
  const [infoItem, setInfoItem] = React.useState(null);
  const abrirPdfColppy = async (idFactura, idCliente) => {
    try {
      toast.loading('Descargando PDF...', { id: 'colppy-pdf' });
      const res = await colppyApi.getInvoicePdfBlob(idFactura, idCliente);
      const url = URL.createObjectURL(res.data);
      toast.dismiss('colppy-pdf');
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('No se pudo descargar el PDF de Colppy', { id: 'colppy-pdf' });
    }
  };
  return (
        <TabsContent value="factura" className="mt-0">
          <ContratoInfoDialog item={infoItem} onClose={() => setInfoItem(null)} />
          {/* Cartel persistente de error de emisión Colppy (legible + copiable) */}
          {colppyError && (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
              data-testid="colppy-error-modal"
              onClick={() => setColppyError(null)}
            >
              <div
                className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-3"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    No se pudo emitir la factura en Colppy
                  </h3>
                  <button
                    onClick={() => setColppyError(null)}
                    className="text-slate-400 hover:text-slate-700"
                    data-testid="colppy-error-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-slate-700">{colppyError.message}</p>
                {colppyError.detail && (
                  <div className="rounded bg-red-50 border border-red-200 p-3">
                    <p className="text-xs font-semibold text-red-800 mb-1">Motivo devuelto por Colppy:</p>
                    <p className="text-sm text-red-900 break-words" data-testid="colppy-error-detail">{colppyError.detail}</p>
                  </div>
                )}
                {colppyError.raw && (
                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer">Ver respuesta completa (técnico)</summary>
                    <pre className="mt-2 max-h-48 overflow-auto bg-slate-50 border rounded p-2 whitespace-pre-wrap break-words">{colppyError.raw}</pre>
                  </details>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="colppy-error-copy"
                    onClick={() => {
                      navigator.clipboard?.writeText(
                        `${colppyError.message}\n\nMotivo: ${colppyError.detail || '-'}\n\n${colppyError.raw || ''}`
                      );
                      toast.success('Error copiado al portapapeles');
                    }}
                  >
                    Copiar error
                  </Button>
                  <Button size="sm" onClick={() => setColppyError(null)}>Cerrar</Button>
                </div>
              </div>
            </div>
          )}
          {/* Selector de período */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="w-[140px]">
                <PeriodSelector
                  value={selectedPeriodo}
                  onChange={setSelectedPeriodo}
                  periods={getFacturacionPeriods()}
                  placeholder="Período"
                />
              </div>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <div className="ml-2 flex items-center"><EmailConfigButton /><HistorialNetoButton edificioId={edificioId} periodo={selectedPeriodo} /></div>
            </div>
          </div>
          {loadingFacturacion ? (
            <LoadingState text="Cargando datos de facturación..." />
          ) : (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Tabla de facturación - siempre visible con headers */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-auto flex-1 min-h-0">
                <table className="w-full text-sm min-w-[1100px] min-h-full [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-10 [&_tfoot_td]:bg-slate-100 [&_tfoot_td]:shadow-[0_-2px_0_0_#cbd5e1]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Unidad</th>
                      <th className="py-2.5 px-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[14%]">Inquilino</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">OC</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Historial FC</th>
                      <th className="py-2.5 px-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Monto Neto</th>
                      <th className="py-2.5 px-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Intereses</th>
                      <th className="py-2.5 px-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">IVA</th>
                      <th className="py-2.5 px-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Revisión</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Facturas</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">PDF</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider" title="Cómo se entrega la factura (definido en el contrato, sección fiscal)">Gestión</th>
                      <th className="py-2.5 px-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Envía factura</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(!facturacionData || facturacionData.items.length === 0) ? (
                      <tr>
                        <td colSpan={13} className="py-8 text-center text-slate-500">
                          Sin unidades en administración para este período
                        </td>
                      </tr>
                    ) : (
                    getGroupedFacItems(
                      facturacionData.items.filter(item => {
                        if (filtroEstadoFactura === 'todos') return true;
                        const estadoItem = item.estado_factura || 'pendiente';
                        return estadoItem === filtroEstadoFactura;
                      })
                    ).map((entry, idx) => {
                      if (entry.type === 'group_summary') {
                        const items = entry.items;
                        const sumNeto = items.reduce((s, i) => s + (i.monto_neto || 0), 0);
                        const sumIntereses = items.reduce((s, i) => s + (i.intereses || 0), 0);
                        const sumIva = items.reduce((s, i) => s + (i.iva || 0), 0);
                        const sumTotal = items.reduce((s, i) => s + (i.total || 0), 0);
                        const isExpanded = expandedGrupos[entry.grupo.grupo_id];
                        return (
                          <tr key={`gs-${entry.grupo.grupo_id}`} className="bg-indigo-50 hover:bg-indigo-100/60 font-semibold cursor-pointer" onClick={() => toggleGrupoExpanded(entry.grupo.grupo_id)}>
                            <td className="py-2.5 px-3 text-indigo-900">
                              <span className="inline-flex items-center gap-1.5">
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                {entry.grupo.nombre}
                              </span>
                            </td>
                            <td className="py-2.5 px-2 text-xs text-indigo-500">{items.length} unidades</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sumNeto, 2)}</td>
                            <td className="py-2.5 px-2 text-right font-mono text-sm text-indigo-400">{sumIntereses > 0 ? `$ ${formatImporte(sumIntereses, 2)}` : '-'}</td>
                            <td className="py-2.5 px-2 text-right font-mono text-sm text-indigo-700 whitespace-nowrap">$ {formatImporte(sumIva, 2)}</td>
                            <td className="py-2.5 px-2 text-right font-mono text-sm text-green-700 whitespace-nowrap">$ {formatImporte(sumTotal, 2)}</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                            <td className="py-2.5 px-2 text-center text-xs text-indigo-400">-</td>
                          </tr>
                        );
                      }
                      const item = entry.item;
                      const perteneceAGrupo = !!entry.grupoId;
                      
                      return (
                      <tr key={item.unidad_id} className={cn(
                        "hover:bg-slate-100/50",
                        perteneceAGrupo && "bg-indigo-50/40"
                      )}>
                        <td className={cn(
                          "py-2 px-3 font-medium",
                          perteneceAGrupo && "border-l-4 border-l-indigo-400"
                        )}>
                          {perteneceAGrupo && <span className="inline-block w-4"></span>}
                          <span className={cn(perteneceAGrupo ? "text-indigo-800" : "text-slate-900")}>
                            {item.unidad_nombre}
                          </span>
                          {item.error_calculo && (
                            <div className="mt-1">
                              <span
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full cursor-help align-middle"
                                title={item.error_calculo}
                                data-testid={`error-calculo-badge-${item.unidad_id}`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Falta índice
                              </span>
                              <div className="text-[11px] text-red-600 mt-0.5 leading-tight" data-testid={`error-calculo-detalle-${item.unidad_id}`}>
                                {item.error_calculo}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {item.tiene_contrato ? (
                            <button type="button" onClick={() => setInfoItem(item)} title={item.nombre_comercial || item.inquilino || ''}
                              className="block max-w-[200px] truncate text-left text-sm text-slate-700 hover:text-blue-700 hover:underline"
                              data-testid={`inquilino-info-btn-${item.unidad_id}`}>
                              {item.nombre_comercial || item.inquilino || '-'}
                            </button>
                          ) : (
                            <span className="text-amber-600 text-xs">Sin contrato</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const contrato = contratos?.find(c => c.unidad_id === item.unidad_id || c.unidades_contrato?.some(uc => uc.unidad_id === item.unidad_id));
                            if (!contrato || !contrato.orden_compra_tipo) return <span className="text-slate-300 text-xs">-</span>;
                            if (contrato.orden_compra_tipo === 'anual') {
                              return <span className="text-xs font-mono text-slate-700">{contrato.orden_compra || '-'}</span>;
                            }
                            // Mensual: editable per month
                            return (
                              <OcMensualCell
                                item={item}
                                edificioId={edificioId}
                                selectedPeriodo={selectedPeriodo}
                                setFacturacionData={setFacturacionData}
                                toast={toast}
                              />
                            );
                          })()}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {(item.tiene_contrato || item.es_cowork_suscriptor)
                            ? <HistorialFCButton edificioId={edificioId} periodo={selectedPeriodo} item={item} />
                            : <span className="text-slate-300 text-xs">-</span>}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-sm whitespace-nowrap">
                          {item.tiene_contrato ? (
                            <MontoNetoCell
                              item={item}
                              edificioId={edificioId}
                              selectedPeriodo={selectedPeriodo}
                              setFacturacionData={setFacturacionData}
                              toast={toast}
                              formatImporte={formatImporte}
                            />
                          ) : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-sm whitespace-nowrap">
                          {(item.intereses > 0 || (item.intereses_manual !== null && item.intereses_manual !== undefined && item.intereses_manual >= 0)) ? (
                            <InteresesCell
                              item={item}
                              edificioId={edificioId}
                              selectedPeriodo={selectedPeriodo}
                              setFacturacionData={setFacturacionData}
                              toast={toast}
                              formatImporte={formatImporte}
                            />
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-sm whitespace-nowrap">
                          {item.tiene_contrato ? (
                            item.aplica_iva ? (
                              <span className="text-slate-600">
                                $ {formatImporte(item.iva, 2)}
                              </span>
                            ) : (
                              <span
                                className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500"
                                title={`Sin IVA — condición fiscal: ${item.condicion_iva || 'no especificada'}`}
                                data-testid={`iva-exento-${item.unidad_id || item.contrato_id}`}
                              >
                                {item.condicion_iva === 'exento' ? 'Exento'
                                  : item.condicion_iva === 'monotributo' ? 'Monotributo'
                                  : item.condicion_iva === 'consumidor_final' ? 'Cons. Final'
                                  : 'Sin IVA'}
                              </span>
                            )
                          ) : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-sm font-semibold whitespace-nowrap">
                          {item.tiene_contrato ? (
                            <span className="text-green-700">
                              $ {formatImporte(item.total, 2)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {item.tiene_contrato ? (
                            item.aprobacion === 'aprobada' ? (
                              (() => {
                                return (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full" data-testid={`revision-badge-${item.unidad_id}`}>
                                    <Check className="w-3 h-3" />
                                    {item.fecha_aprobacion ? formatDate(item.fecha_aprobacion) : 'Revisada'}
                                    <button
                                      className="ml-1 text-green-500 hover:text-red-500 transition-colors"
                                      title="Deshacer revisión"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          // Si tiene factura manual, eliminarla primero
                                          if (item.factura_tipo === 'manual') {
                                            await facturacionApi.deleteFactura(edificioId, item.unidad_id, selectedPeriodo);
                                          }
                                          await facturacionApi.updateStatus(
                                            edificioId, item.unidad_id, selectedPeriodo,
                                            { aprobacion: 'pendiente' }
                                          );
                                          setFacturasCreadas(prev => {
                                            const next = { ...prev };
                                            delete next[`${item.unidad_id}_${selectedPeriodo}`];
                                            return next;
                                          });
                                          const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                          setFacturacionData(res.data);
                                        } catch (err) {
                                          toast.error('Error al deshacer revisión');
                                        }
                                      }}
                                      data-testid={`deshacer-revision-btn-${item.unidad_id}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                );
                              })()
                            ) : item.error_calculo && !(item.monto_manual !== null && item.monto_manual !== undefined && item.monto_manual >= 0) ? (
                              <span
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full cursor-help"
                                title={item.error_calculo}
                                data-testid={`aprobar-bloqueado-${item.unidad_id}`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Error de cálculo
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={async () => {
                                  try {
                                    await facturacionApi.updateStatus(
                                      edificioId,
                                      item.unidad_id,
                                      selectedPeriodo,
                                      { aprobacion: 'aprobada' }
                                    );
                                    const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                    setFacturacionData(res.data);
                                    toast.success('Factura revisada');
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Error al marcar revisada');
                                  }
                                }}
                                data-testid={`revisar-btn-${item.unidad_id}`}
                              >
                                Aprobar
                              </Button>
                            )
                          ) : '-'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {/* Columna Facturas: Emitida (Colppy o Manual) o botones Emitir/Subir */}
                          {item.factura_tipo === 'manual' ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full" data-testid={`factura-manual-badge-${item.unidad_id}`}>
                              <Upload className="w-3 h-3" />
                              Manual
                            </span>
                          ) : (item.colppy_emitida || item.factura_tipo === 'colppy') ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full" data-testid={`factura-colppy-badge-${item.unidad_id}`} title={item.colppy_cae ? `CAE ${item.colppy_cae}` : ''}>
                              <Check className="w-3 h-3" />
                              Emitida{item.colppy_nro_factura ? ` ${item.colppy_nro_factura}` : ' (Colppy)'}
                            </span>
                          ) : facturasCreadas[`${item.unidad_id}_${selectedPeriodo}`] ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full" data-testid={`factura-emitida-badge-${item.unidad_id}`}>
                              <Check className="w-3 h-3" />
                              {new Date(facturasCreadas[`${item.unidad_id}_${selectedPeriodo}`].fecha).toLocaleDateString('es-AR')}
                            </span>
                          ) : item.aprobacion === 'aprobada' ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!item.cuit) { toast.error('El contrato no tiene CUIT configurado'); return; }
                                  if (!item.monto_neto || item.monto_neto <= 0) { toast.error('El monto neto debe ser mayor a 0'); return; }
                                  let tipoFactura = 'A';
                                  if (item.condicion_iva === 'monotributo' || item.condicion_iva === 'exento' || item.condicion_iva === 'consumidor_final') tipoFactura = 'B';
                                  try {
                                    toast.loading('Emitiendo factura en Colppy...', { id: 'colppy-invoice' });
                                    const montoNeto = item.monto_neto || 0;
                                    const ivaCalculado = item.iva || Math.round(montoNeto * 0.21 * 100) / 100;
                                    const totalCalculado = item.total || (montoNeto + ivaCalculado);
                                    // Construir descripción con OC si corresponde
                                    let descripcion = `Alquiler ${item.unidad_nombre} - ${selectedPeriodo}`;
                                    if (item.orden_compra_tipo === 'anual' && item.orden_compra) {
                                      descripcion += ` - OC ${item.orden_compra}`;
                                    } else if (item.orden_compra_tipo === 'mensual' && item.orden_compra_mensual) {
                                      descripcion += ` - OC ${item.orden_compra_mensual}`;
                                    }
                                    let response;
                                    try {
                                      response = await colppyApi.createInvoice({
                                        cuit: item.cuit, razon_social: item.razon_social || item.inquilino,
                                        descripcion,
                                        monto_neto: montoNeto, iva: ivaCalculado, total: totalCalculado,
                                        tipo_factura: tipoFactura, condicion_pago: 'Cuenta Corriente',
                                        unidad_id: item.unidad_id, periodo: selectedPeriodo, contrato_id: item.contrato_id
                                      });
                                    } catch (err) {
                                      // Error de conexión/HTTP con nuestro backend: la factura NO se creó
                                      const detail = err.response?.data?.detail;
                                      console.error('[Colppy] Excepción emisión:', err.response?.data || err);
                                      toast.error('No se pudo emitir la factura', { id: 'colppy-invoice' });
                                      setColppyError({
                                        message: 'No se pudo emitir la factura en Colppy (error de conexión con el servidor).',
                                        detail: typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : String(err?.message || 'Error desconocido')),
                                        raw: err.response?.data ? JSON.stringify(err.response.data, null, 2) : null,
                                      });
                                      return;
                                    }

                                    if (!response.data.ok) {
                                      // Colppy rechazó la emisión (la factura NO se creó)
                                      const detalle = response.data.error_detail;
                                      const rawColppy = response.data.response_colppy;
                                      const rawMensaje = rawColppy?.response?.message || rawColppy?.result?.mensaje;
                                      const msg = response.data.message || 'Error al emitir factura';
                                      console.error('[Colppy] Error emisión:', response.data);
                                      toast.error(msg, { id: 'colppy-invoice' });
                                      setColppyError({
                                        message: msg,
                                        detail: detalle || rawMensaje || null,
                                        raw: rawColppy ? JSON.stringify(rawColppy, null, 2) : null,
                                      });
                                      return;
                                    }

                                    // Emisión OK: la factura se creó en Colppy
                                    const factura = response.data.factura_creada;
                                    toast.success(`Factura ${tipoFactura} emitida: ID ${factura?.id_factura}`, { id: 'colppy-invoice' });
                                    // Guardado posterior: si falla, NO es un error de emisión
                                    try {
                                      await facturacionApi.updateStatus(edificioId, item.unidad_id, selectedPeriodo, { factura_tipo: 'colppy' });
                                      setFacturasCreadas(prev => ({
                                        ...prev, [`${item.unidad_id}_${selectedPeriodo}`]: {
                                          id_factura: factura?.id_factura, id_cliente: response.data.cliente?.idCliente,
                                          cae: factura?.cae, fecha: new Date().toISOString(), tipo: 'colppy'
                                        }
                                      }));
                                      const facRes = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                      setFacturacionData(facRes.data);
                                    } catch (bkErr) {
                                      console.error('[Colppy] Factura emitida pero falló la actualización local:', bkErr);
                                      toast.success('La factura se emitió en Colppy. Recargá la página para ver el estado actualizado.', { id: 'colppy-invoice', duration: 8000 });
                                    }
                                  } catch (err) {
                                    console.error('[Colppy] Error inesperado al emitir:', err);
                                    toast.error('Error inesperado al emitir la factura', { id: 'colppy-invoice' });
                                  }
                                }}
                                data-testid={`emitir-factura-btn-${item.unidad_id}`}
                              >
                                <Zap className="w-3 h-3 mr-1" />
                                Emitir
                              </Button>
                              <label className="cursor-pointer" data-testid={`subir-factura-btn-${item.unidad_id}`}>
                                <input
                                  type="file"
                                  accept=".pdf"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (!file.name.toLowerCase().endsWith('.pdf')) {
                                      toast.error('Solo se permiten archivos PDF');
                                      return;
                                    }
                                    try {
                                      toast.loading('Subiendo factura...', { id: 'upload-factura' });
                                      const response = await facturacionApi.uploadFactura(edificioId, item.unidad_id, selectedPeriodo, file);
                                      if (response.data.ok) {
                                        toast.success('Factura subida correctamente', { id: 'upload-factura' });
                                        const facRes = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                        setFacturacionData(facRes.data);
                                      }
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Error al subir factura', { id: 'upload-factura' });
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <span className="inline-flex items-center gap-1 h-7 px-2 text-xs border border-slate-200 rounded-md hover:bg-slate-50 transition-colors">
                                  <Upload className="w-3 h-3" />
                                  Subir
                                </span>
                              </label>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Aprobar primero</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {/* PDF - Manual upload o Colppy */}
                          {item.factura_tipo === 'manual' && item.factura_archivo_id ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => {
                                const pdfUrl = facturacionApi.getFacturaPdfUrl(item.factura_archivo_id);
                                window.open(pdfUrl, '_blank');
                              }}
                              title={item.factura_nombre_archivo || 'Ver PDF'}
                              data-testid={`pdf-manual-btn-${item.unidad_id}`}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          ) : (item.colppy_factura_id && item.colppy_cliente_id) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => abrirPdfColppy(item.colppy_factura_id, item.colppy_cliente_id)}
                              title={`Descargar PDF - Factura ${item.colppy_nro_factura || item.colppy_factura_id}`}
                              data-testid={`pdf-colppy-btn-${item.unidad_id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          ) : item.colppy_emitida ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={async () => {
                                try {
                                  toast.loading('Recuperando PDF de Colppy...', { id: 'colppy-pdf' });
                                  const res = await colppyApi.relinkInvoice(edificioId, item.unidad_id, selectedPeriodo);
                                  if (res.data?.ok && res.data.idFactura) {
                                    toast.dismiss('colppy-pdf');
                                    await abrirPdfColppy(res.data.idFactura, res.data.idCliente);
                                    const facRes = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                                    setFacturacionData(facRes.data);
                                  } else {
                                    toast.error('No se encontró el PDF en Colppy', { id: 'colppy-pdf' });
                                  }
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'No se pudo obtener el PDF', { id: 'colppy-pdf' });
                                }
                              }}
                              title="Recuperar y descargar PDF de Colppy"
                              data-testid={`pdf-colppy-relink-btn-${item.unidad_id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          ) : facturasCreadas[`${item.unidad_id}_${selectedPeriodo}`]?.id_factura ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => {
                                const facturaData = facturasCreadas[`${item.unidad_id}_${selectedPeriodo}`];
                                const pdfUrl = colppyApi.getInvoicePdfUrl(facturaData.id_factura, facturaData.id_cliente);
                                window.open(pdfUrl, '_blank');
                              }}
                              title={`Descargar PDF - Factura ${facturasCreadas[`${item.unidad_id}_${selectedPeriodo}`]?.id_factura}`}
                              data-testid={`pdf-btn-${item.unidad_id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <MetodoFacturaBadge metodo={item.facturacion_metodo} contratoId={item.es_cowork_suscriptor ? null : item.contrato_id} portalUrl={item.contrato_info?.proveedor_plataforma_url} testId={`gestion-factura-${item.unidad_id}`} />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <EnviarFacturaCell
                            item={item}
                            edificioId={edificioId}
                            periodo={selectedPeriodo}
                            tienePdf={!!((item.factura_tipo === 'manual' && item.factura_archivo_id) || (item.colppy_factura_id && item.colppy_cliente_id))}
                            onSent={async () => {
                              const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
                              setFacturacionData(res.data);
                            }}
                          />
                        </td>
                      </tr>
                    );
                    })
                    )}
                    {facturacionData && facturacionData.items.length > 0 && (
                      <tr aria-hidden="true" className="h-full">
                        <td colSpan={12} className="p-0 border-0"></td>
                      </tr>
                    )}
                  </tbody>
                  {facturacionData && facturacionData.items.length > 0 && (
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                    <tr className="font-semibold">
                      <td colSpan={4} className="py-3 px-2"></td>
                      <td className="py-3 px-2 text-right font-mono text-slate-900 whitespace-nowrap">
                        $ {formatImporte(facturacionData.totales.neto, 2)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-orange-600 whitespace-nowrap">
                        {facturacionData.totales.intereses > 0 ? `$ ${formatImporte(facturacionData.totales.intereses, 2)}` : '-'}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-slate-700 whitespace-nowrap">
                        $ {formatImporte(facturacionData.totales.iva, 2)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-green-700 whitespace-nowrap">
                        $ {formatImporte(facturacionData.totales.total, 2)}
                      </td>
                      <td colSpan={5} className="py-3 px-2"></td>
                    </tr>
                  </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </TabsContent>
  );
};
